/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TableTokenAuctions } from '@components/tables/token-auctions';
import { TableTokenOffers } from '@components/tables/token-offers';
import { TableTokenSwaps } from '@components/tables/token-swaps';
import { IToken } from '@interface/token';
import { actions } from '@state/modals';
import { RootState } from '@state/store';
import { getViewerEditionsOutsideMarket } from '@utils/token';
import { getScreenName } from '@utils/user';
import { Box, Button, Text } from '@versumstudios/ui';

interface IMarket {
  token: IToken;
}

export const Market: FC<IMarket> = ({ token }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const viewer = auth.user?.id;
  const { holders, swaps, offers, auctions, creators } = useSelector((state: RootState) => state.token);
  const { token_id, contract } = token;
  const viewerOwnedEditions = getViewerEditionsOutsideMarket(viewer!, holders);

  const parsedSwaps = swaps.map((user: any) => {
    const { seller } = user;
    return {
      ...user,
      // seller
      sellerScreenName: getScreenName(
        seller.user[0].user.verified_on_chain,
        seller.user[0].user.screen_name,
        seller.user[0].user.id,
        seller.user[0].user.name
      ),
      sellerIsCreator: creators.filter((e: any) => e.wallet.address === seller.address).length > 0,
      sellerIsYou: seller.address === viewer,
    };
  });

  const parsedAuctions = auctions.map((user: any) => {
    const { seller, current_bidder } = user;
    return {
      ...user,
      // seller
      sellerScreenName: getScreenName(
        seller.user[0].user.verified_on_chain,
        seller.user[0].user.screen_name,
        seller.user[0].user.id,
        seller.user[0].user.name
      ),
      sellerIsCreator: creators.filter((e: any) => e.wallet.address === seller.address).length > 0,
      sellerIsYou: seller.address === viewer,
      // highest bidder (if present)
      ...(current_bidder && {
        bidderScreenName: getScreenName(
          current_bidder.user[0].user.verified_on_chain,
          current_bidder.user[0].user.screen_name,
          current_bidder.user[0].user.id,
          current_bidder.user[0].user.name
        ),
        bidderIsCreator: creators.filter((e: any) => e.wallet.address === current_bidder.user[0].user.id).length > 0,
        bidderIsYou: current_bidder.user[0].user.id === viewer,
      }),
    };
  });

  const parsedOffers = offers.map((user: any) => {
    const { buyer } = user;

    return {
      ...user,
      // buyer
      buyerScreenName: getScreenName(
        buyer.user[0].user.verified_on_chain,
        buyer.user[0].user.screen_name,
        buyer.user[0].user.id,
        buyer.user[0].user.name
      ),
      buyerIsCreator: creators.filter((e: any) => e.wallet.address === buyer.address).length > 0,
      buyerIsYou: buyer.address === viewer,
    };
  });

  const handleCreateSwap = () => {
    dispatch({
      type: actions.MODALS_SET_MODAL,
      payload: {
        type: 'swap-create',
        platformName: contract.name,
        tokenKT: contract.wallet?.address,
        tokenId: token_id,
        tokensOwned: viewerOwnedEditions,
        creatorsTotal: token.creators.length,
      },
    });
  };

  const handleCreateAuction = () => {
    dispatch({
      type: actions.MODALS_SET_MODAL,
      payload: {
        type: 'auction-create',
        platformName: contract.name,
        tokenKT: contract.wallet?.address,
        tokenId: token_id,
        creatorsTotal: token.creators.length,
      },
    });
  };

  const handleTransfer = () => {
    dispatch({
      type: actions.MODALS_SET_MODAL,
      payload: {
        type: 'token-transfer',
        platformName: contract.name,
        tokenKT: contract.wallet?.address,
        tokenId: token_id,
        tokensOwned: viewerOwnedEditions,
      },
    });
  };

  const handleBurn = () => {
    dispatch({
      type: actions.MODALS_SET_MODAL,
      payload: {
        type: 'token-burn',
        platformName: contract.name,
        tokenKT: contract.wallet?.address,
        tokenId: token_id,
        tokensOwned: viewerOwnedEditions,
      },
    });
  };

  const getCopy = (quantity: number) => {
    switch (quantity) {
      case 1:
        return `You own ${quantity} edition of "${token.title}".`;
      default:
        return `You own ${quantity} editions of "${token.title}".`;
    }
  };

  return (
    <Box css={{ flexDirection: 'column', minHeight: 256 }}>
      {viewerOwnedEditions > 0 && (
        <>
          <Text css={{ color: '$secondaryFill' }}>{getCopy(viewerOwnedEditions)}</Text>
          <Box
            css={{
              marginBottom: '$space$1',
              '& > *': { marginRight: '$space$4' },
            }}
          >
            <Button variant="market" onClick={handleCreateSwap}>
              Create Swap
            </Button>
            <Button variant="market" onClick={handleCreateAuction}>
              Create Auction
            </Button>
            <Button variant="market" onClick={handleTransfer}>
              Transfer
            </Button>
            <Button variant="market" onClick={handleBurn}>
              Burn
            </Button>
          </Box>
        </>
      )}

      {auctions.length > 0 && (
        <Box
          css={{
            flexDirection: 'column',
            marginBottom: '$space$1',
          }}
        >
          <Text as="h6">Auctions</Text>
          <TableTokenAuctions data={parsedAuctions} />
        </Box>
      )}

      {swaps.length > 0 && (
        <Box
          css={{
            flexDirection: 'column',
            marginBottom: '$space$1',
          }}
        >
          <Text as="h6">Swaps</Text>
          <TableTokenSwaps data={parsedSwaps} />
        </Box>
      )}

      {offers.length > 0 && (
        <Box
          css={{
            flexDirection: 'column',
            marginBottom: '$space$1',
          }}
        >
          <Text as="h6">Offers</Text>
          <TableTokenOffers data={parsedOffers} />
        </Box>
      )}

      {viewerOwnedEditions === 0 && swaps.length === 0 && auctions.length === 0 && offers.length === 0 && (
        <Text css={{ color: '$secondaryFill' }}>This token is not for sale.</Text>
      )}
    </Box>
  );
};
